/**
 * Shared Field Options
 *
 * Canonical source of truth for standard field options (employee count ranges,
 * country codes) used by both the frontend dialogs and the backend import handler.
 * Single source of truth - update here only.
 */
/** Employee count range options - must match CreateClientModal.tsx */
export const EMPLOYEE_COUNT_RANGES = [
    { min: 1, max: 10, value: '1-10', label: '1-10' },
    { min: 11, max: 50, value: '11-50', label: '11-50' },
    { min: 51, max: 200, value: '51-200', label: '51-200' },
    { min: 201, max: 500, value: '201-500', label: '201-500' },
    { min: 501, max: 1000, value: '501-1000', label: '501-1000' },
    { min: 1001, max: 5000, value: '1001-5000', label: '1001-5000' },
    { min: 5001, max: Infinity, value: '5001+', label: '5001+' },
];
/** Select options for UI dropdowns */
export const EMPLOYEE_COUNT_OPTIONS = [
    { value: '', label: 'Select employee count' },
    ...EMPLOYEE_COUNT_RANGES.map((r) => ({ value: r.value, label: r.label })),
];
/**
 * Normalize employee count to a predefined range.
 * Accepts: number, numeric string, or existing range string.
 * Examples: "5098" → "5001+", "500" → "201-500", "11-50" → "11-50"
 */
export function normalizeEmployeeCount(raw) {
    // Already a valid range
    if (EMPLOYEE_COUNT_RANGES.some((r) => r.value === raw))
        return raw;
    const num = parseInt(raw.replace(/[^0-9]/g, ''), 10);
    if (isNaN(num) || num < 1)
        return raw; // Can't normalize, return as-is
    for (const range of EMPLOYEE_COUNT_RANGES) {
        if (num >= range.min && num <= range.max)
            return range.value;
    }
    return raw;
}
/** ISO 3166-1 alpha-2 country list */
export const COUNTRIES = [
    { code: 'AF', name: 'Afghanistan' }, { code: 'AL', name: 'Albania' },
    { code: 'DZ', name: 'Algeria' }, { code: 'AR', name: 'Argentina' },
    { code: 'AU', name: 'Australia' }, { code: 'AT', name: 'Austria' },
    { code: 'BD', name: 'Bangladesh' }, { code: 'BE', name: 'Belgium' },
    { code: 'BR', name: 'Brazil' }, { code: 'BG', name: 'Bulgaria' },
    { code: 'CA', name: 'Canada' }, { code: 'CL', name: 'Chile' },
    { code: 'CN', name: 'China' }, { code: 'CO', name: 'Colombia' },
    { code: 'HR', name: 'Croatia' }, { code: 'CZ', name: 'Czechia' },
    { code: 'DK', name: 'Denmark' }, { code: 'EG', name: 'Egypt' },
    { code: 'EE', name: 'Estonia' }, { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' }, { code: 'DE', name: 'Germany' },
    { code: 'GR', name: 'Greece' }, { code: 'HK', name: 'Hong Kong' },
    { code: 'HU', name: 'Hungary' }, { code: 'IN', name: 'India' },
    { code: 'ID', name: 'Indonesia' }, { code: 'IE', name: 'Ireland' },
    { code: 'IL', name: 'Israel' }, { code: 'IT', name: 'Italy' },
    { code: 'JP', name: 'Japan' }, { code: 'KE', name: 'Kenya' },
    { code: 'KR', name: 'South Korea' }, { code: 'LV', name: 'Latvia' },
    { code: 'LT', name: 'Lithuania' }, { code: 'MY', name: 'Malaysia' },
    { code: 'MX', name: 'Mexico' }, { code: 'MA', name: 'Morocco' },
    { code: 'NL', name: 'Netherlands' }, { code: 'NZ', name: 'New Zealand' },
    { code: 'NG', name: 'Nigeria' }, { code: 'NO', name: 'Norway' },
    { code: 'PK', name: 'Pakistan' }, { code: 'PE', name: 'Peru' },
    { code: 'PH', name: 'Philippines' }, { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' }, { code: 'RO', name: 'Romania' },
    { code: 'RU', name: 'Russia' }, { code: 'SA', name: 'Saudi Arabia' },
    { code: 'SG', name: 'Singapore' }, { code: 'SK', name: 'Slovakia' },
    { code: 'SI', name: 'Slovenia' }, { code: 'ZA', name: 'South Africa' },
    { code: 'ES', name: 'Spain' }, { code: 'SE', name: 'Sweden' },
    { code: 'CH', name: 'Switzerland' }, { code: 'TW', name: 'Taiwan' },
    { code: 'TH', name: 'Thailand' }, { code: 'TR', name: 'Turkey' },
    { code: 'UA', name: 'Ukraine' }, { code: 'AE', name: 'United Arab Emirates' },
    { code: 'GB', name: 'United Kingdom' }, { code: 'US', name: 'United States' },
    { code: 'VN', name: 'Vietnam' },
];
/** Select options for UI dropdowns */
export const COUNTRY_SELECT_OPTIONS = [
    { value: '', label: 'Select country' },
    ...COUNTRIES.map((c) => ({ value: c.code, label: c.name })),
];
// Build lookup maps for country normalization
const COUNTRY_NAME_TO_CODE = new Map(COUNTRIES.map((c) => [c.name.toLowerCase(), c.code]));
const COUNTRY_CODE_SET = new Set(COUNTRIES.map((c) => c.code));
// Common country aliases
const COUNTRY_ALIASES = {
    'usa': 'US', 'u.s.a.': 'US', 'u.s.': 'US', 'america': 'US',
    'uk': 'GB', 'u.k.': 'GB', 'england': 'GB',
    'uae': 'AE', 'u.a.e.': 'AE',
    'south korea': 'KR', 'korea': 'KR',
    'czech republic': 'CZ',
};
/**
 * Normalize country to ISO alpha-2 code.
 * Accepts: "US", "United States", "USA", "united states" → "US"
 */
export function normalizeCountry(raw) {
    const trimmed = raw.trim();
    // Already a valid code
    if (COUNTRY_CODE_SET.has(trimmed.toUpperCase()))
        return trimmed.toUpperCase();
    // Match by name (case-insensitive)
    const code = COUNTRY_NAME_TO_CODE.get(trimmed.toLowerCase());
    if (code)
        return code;
    // Common aliases
    return COUNTRY_ALIASES[trimmed.toLowerCase()] ?? trimmed;
}
/** Common ISO 4217 currency codes */
export const CURRENCIES = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
    { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
    { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
    { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
    { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
    { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
    { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
    { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
    { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
    { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
];
/** Select options for UI dropdowns */
export const CURRENCY_SELECT_OPTIONS = [
    { value: '', label: 'Select currency' },
    ...CURRENCIES.map((c) => ({ value: c.code, label: `${c.code} (${c.symbol}) - ${c.name}` })),
];
const CURRENCY_MAP = new Map(CURRENCIES.map((c) => [c.code, c]));
/** Look up a currency symbol by ISO 4217 code. Falls back to the code itself. */
export function getCurrencySymbol(code) {
    if (!code)
        return '$';
    return CURRENCY_MAP.get(code)?.symbol ?? code;
}
// =============================================================================
// Custom Field Normalization
// =============================================================================
/**
 * Normalize a string for fuzzy matching: lowercase, remove spaces/underscores/hyphens.
 * "Profit Margin" → "profitmargin", "profit_margin" → "profitmargin"
 */
export function normalizeKey(s) {
    return s.toLowerCase().replace(/[\s_\-]+/g, '');
}
/**
 * Fuzzy match a value against predefined options.
 * Tries: exact match → lowercase match → normalized match → substring match
 */
export function normalizeSelectOption(value, options) {
    const valueLabels = options.map((o) => o.value);
    // Exact match on value
    if (valueLabels.includes(value))
        return value;
    // Case-insensitive match on value
    const lower = value.toLowerCase();
    const caseMatch = options.find((o) => o.value.toLowerCase() === lower);
    if (caseMatch)
        return caseMatch.value;
    // Normalized match on value
    const normalized = normalizeKey(value);
    const normMatch = options.find((o) => normalizeKey(o.value) === normalized);
    if (normMatch)
        return normMatch.value;
    // Match on label (case-insensitive)
    const labelMatch = options.find((o) => o.label.toLowerCase() === lower);
    if (labelMatch)
        return labelMatch.value;
    // Normalized match on label
    const labelNormMatch = options.find((o) => normalizeKey(o.label) === normalized);
    if (labelNormMatch)
        return labelNormMatch.value;
    // Substring match
    const subMatch = options.find((o) => o.label.toLowerCase().includes(lower) || lower.includes(o.label.toLowerCase()));
    if (subMatch)
        return subMatch.value;
    // No match - return as-is
    return value;
}
/**
 * Normalize a custom field value to match its predefined options.
 * For single_select: fuzzy match to find the closest option value.
 * For multi_select: normalize each value.
 */
export function normalizeCustomFieldValue(value, fieldType, options) {
    if (!options || options.length === 0)
        return value;
    if (fieldType !== 'single_select' && fieldType !== 'multi_select')
        return value;
    // For multi_select, split by comma and normalize each
    if (fieldType === 'multi_select') {
        return value
            .split(',')
            .map((v) => normalizeSelectOption(v.trim(), options))
            .join(', ');
    }
    // Single select
    return normalizeSelectOption(value, options);
}
